import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import { Menu, X, ChevronDown, Globe } from "lucide-react";

/** Click-to-open language menu; closes on outside click. */
const LangSwitcher = ({ className = "" }) => {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const current = languages.find((l) => l.code === lang);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        data-testid="lang-switcher"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-sm text-[#5f6b57] hover:text-[#1d3a1f] transition-colors duration-200"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={15} /> {current?.label}
      </button>
      {open && (
        <div data-testid="lang-menu" role="listbox" className="absolute end-0 top-full mt-2 w-40 bg-white border border-[#1d3a1f]/10 rounded-xl shadow-xl p-1.5 z-50">
          {languages.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              role="option"
              aria-selected={lang === l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`block w-full text-start px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                lang === l.code ? "text-[#b98a26] font-bold bg-[#d3a53a]/10" : "text-[#5f6b57] hover:text-[#1d3a1f] hover:bg-[#1d3a1f]/5"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Fixed, glassy header with a Ventures dropdown (desktop) / accordion (mobile). */
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [venturesOpen, setVenturesOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setVenturesOpen(false); }, [pathname]);

  const dropdown = [
    { label: t("nav.fruit"), to: "/fruit-trading" },
    { label: t("nav.rootstock"), to: "/rootstock" },
    { label: t("nav.mango"), to: "/mango-export" },
  ];
  const links = [
    { label: t("nav.story"), to: "/about", id: "story" },
    { label: t("nav.sourcing"), to: "/sourcing", id: "sourcing" },
    { label: t("nav.partners"), to: "/partners", id: "partners" },
    { label: t("nav.quality"), to: "/quality", id: "quality" },
    { label: t("nav.insights"), to: "/insights", id: "insights" },
    { label: t("nav.contact"), to: "/contact", id: "contact" },
  ];

  const linkBase = "text-sm text-[#5f6b57] hover:text-[#1d3a1f] transition-colors duration-200";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-shadow duration-300 backdrop-blur-xl bg-[#f7f4ec]/85 border-b border-[#1d3a1f]/10 ${scrolled ? "shadow-[0_4px_20px_rgba(20,48,27,0.06)]" : ""}`}
    >
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-home" className="flex items-center" aria-label="Farmsons home">
          <img src="/farmsons-logo-dark.png" alt="Farmsons logo" className="h-8 md:h-9 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          <div className="relative group">
            <button data-testid="nav-ventures" className={`${linkBase} inline-flex items-center gap-1`}>
              {t("nav.ventures")} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
            </button>
            <div className="absolute start-0 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="w-64 bg-[#ffffff] border border-[#1d3a1f]/10 p-2">
                {dropdown.map((d) => (
                  <NavLink
                    key={d.to}
                    to={d.to}
                    data-testid={`nav-dropdown-${d.to.slice(1)}`}
                    className="block px-4 py-3 text-sm text-[#5f6b57] hover:text-[#d3a53a] hover:bg-[#b98a26]/5 transition-colors duration-200"
                  >
                    {d.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
          {links.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) => `${linkBase} ${isActive ? "!text-[#d3a53a]" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <LangSwitcher />
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="inline-flex bg-[#d3a53a] text-[#173a1e] text-sm font-bold px-6 py-3 rounded-full hover:bg-[#b98a26] transition-colors duration-200"
          >
            {t("common.quote")}
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-4">
          <LangSwitcher />
          <button data-testid="mobile-menu-toggle" className="text-[#1d3a1f]" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#fdfbf5] border-t border-[#1d3a1f]/5 px-6 py-6 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <button onClick={() => setVenturesOpen(!venturesOpen)} className="flex items-center justify-between py-3 text-lg text-[#1d3a1f]">
            {t("nav.ventures")} <ChevronDown size={18} className={venturesOpen ? "rotate-180" : ""} />
          </button>
          {venturesOpen && dropdown.map((d) => (
            <NavLink key={d.to} to={d.to} className="ps-4 py-2 text-[#5f6b57]">{d.label}</NavLink>
          ))}
          {links.map((item) => (
            <NavLink key={item.to} to={item.to} className="py-3 text-lg text-[#1d3a1f]">{item.label}</NavLink>
          ))}
          <Link to="/contact" className="bg-[#d3a53a] text-[#173a1e] font-bold px-6 py-3 mt-3 text-center">{t("common.quote")}</Link>
        </div>
      )}
    </header>
  );
};

export default Nav;
